import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import useDataSchemaVersioning from "@/hooks/use-data-schema-versioning/index";
import usePage from "@/hooks/use-page";
import { useNavigate, useParams } from "@/lib/router-dom";
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import LinkUseCasePersonaModal from "./dsv-link-usecase-persona-modal";

// ============ Helper Functions ============

function formatDate(dateStr) {
	if (!dateStr) return '-';
	const date = new Date(dateStr);
	return date.toISOString().split('T')[0];
}

function getFieldCount(schema) {
	if (!schema) return 0;
	const parsed = typeof schema === 'string' ? JSON.parse(schema) : schema;
	return parsed?.properties ? Object.keys(parsed.properties).length : 0;
}

// ============ Status Badge ============

function StatusBadge({ status }) {
	const statusMap = {
		'ACTIVE': { className: 'dsv-status--active', label: 'Active' },
		'DRAFT': { className: 'dsv-status--draft', label: 'Draft' },
		'DEPRECATED': { className: 'dsv-status--migrating', label: 'Deprecated' },
		'RETIRED': { className: 'dsv-status--retired', label: 'Retired' },
	};
	const config = statusMap[status] || statusMap['DRAFT'];
	return <span className={`dsv-status ${config.className}`}>{config.label}</span>;
}

StatusBadge.propTypes = { status: PropTypes.string };

// ============ Change Type Badge ============

function ChangeTypeBadge({ changeType }) {
	const typeMap = {
		'PATCH': { className: 'dsv-change-type--patch', label: 'Patch' },
		'MINOR': { className: 'dsv-change-type--minor', label: 'Minor' },
		'MAJOR': { className: 'dsv-change-type--major', label: 'Major' },
		'INITIAL': { className: 'dsv-change-type--initial', label: 'Initial' },
	};
	const config = typeMap[changeType] || typeMap['INITIAL'];
	return <span className={`dsv-change-type ${config.className}`}>{config.label}</span>;
}

ChangeTypeBadge.propTypes = { changeType: PropTypes.string };

// ============ Summary Cards ============

function SummaryCards({ currentVersion, status, totalFields, affectedOrganisations }) {
	return (
		<div className="dsv-detail__summary">
			<div className="dsv-detail__summary-card">
				<div className="dsv-detail__summary-card__label">Current Version</div>
				<div className="dsv-detail__summary-card__value">
					{currentVersion}
					<StatusBadge status={status} />
				</div>
			</div>
			<div className="dsv-detail__summary-card">
				<div className="dsv-detail__summary-card__label">Data Fields</div>
				<div className="dsv-detail__summary-card__value">{totalFields}</div>
			</div>
			<div className="dsv-detail__summary-card">
				<div className="dsv-detail__summary-card__label">Affected Organisations</div>
				<div className="dsv-detail__summary-card__value">{affectedOrganisations}</div>
			</div>
		</div>
	);
}

SummaryCards.propTypes = {
	currentVersion: PropTypes.string,
	status: PropTypes.string,
	totalFields: PropTypes.number,
	affectedOrganisations: PropTypes.number,
};

// ============ Version History Item ============

function VersionHistoryItem({ version, onViewSchema }) {
	return (
		<div className="dsv-version-item">
			<div className="dsv-version-item__left">
				<div className="dsv-version-item__header">
					<span className="dsv-version-item__icon">&#9998;</span>
					<span className="dsv-version-item__title">Version {version.version}</span>
					<ChangeTypeBadge changeType={version.changeType} />
					<StatusBadge status={version.status} />
				</div>
				<div className="dsv-version-item__description">
					{version.changeDescription || 'No description'}
				</div>
				<div className="dsv-version-item__meta">
					{version.publishedDate && (
						<span className="dsv-version-item__meta-item">
							Published: {formatDate(version.publishedDate)}
						</span>
					)}
					{version.retiredDate && (
						<span className="dsv-version-item__meta-item">
							Retired: {formatDate(version.retiredDate)}
						</span>
					)}
					{version.expiry && (
						<span className="dsv-version-item__meta-item">
							Expires: {formatDate(version.expiry)}
						</span>
					)}
				</div>
			</div>
			<div className="dsv-version-item__right">
				<button
					className="dsv-btn dsv-btn--outline"
					onClick={() => onViewSchema(version)}
				>
					View Schema
				</button>
			</div>
		</div>
	);
}

VersionHistoryItem.propTypes = {
	version: PropTypes.object,
	onViewSchema: PropTypes.func,
};

// ============ Main Page Component ============

const DSVDetailPage = () => {
	const { goTo } = useNavigate();
	const { dataElementId } = useParams();
	const user = useSelector((state) => state.auth.user);
	const canEditSchema = user?.role?.some((role) =>
		role.name === 'SGTradex Admin' || role.name === 'Super SGTradex Admin'
	);

	const {
		loading,
		currentVersionDetail,
		versionHistory,
		fetchAllVersions,
		impactAnalysis,
		fetchImpactAnalysis,
		fetchDeLinks,
	} = useDataSchemaVersioning();

	const [linkModalVisible, setLinkModalVisible] = useState(false);
	const [hasLink, setHasLink] = useState(true);

	const pageTitle = currentVersionDetail?.elementName || "Data Element Overview";
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{ label: '', link: '/' },
			{ label: 'Data Element', link: '/dsv' },
			{ label: pageTitle, link: '' },
		],
	});

	useEffect(() => {
		if (dataElementId) {
			fetchAllVersions(dataElementId);
		}
	}, [dataElementId]);

	// Fetch impact analysis once we have a current version
	useEffect(() => {
		if (currentVersionDetail?.version && dataElementId) {
			fetchImpactAnalysis(dataElementId, currentVersionDetail.version);
		}
	}, [currentVersionDetail?.version]);

	// Detect existing use-case-persona link so the "Link Use Case" button can hide.
	useEffect(() => {
		if (!dataElementId) return;
		let cancelled = false;
		fetchDeLinks(dataElementId).then((links) => {
			if (cancelled) return;
			setHasLink(Array.isArray(links) && links.length > 0);
		});
		return () => { cancelled = true; };
	}, [dataElementId]);

	// "Pure create" gate: at least one DRAFT version exists and no version has been
	// promoted to ACTIVE / DEPRECATED / RETIRED yet.
	const isDraftOnly = useMemo(() => {
		if (!Array.isArray(versionHistory) || versionHistory.length === 0) return false;
		return versionHistory.every((v) => v.status === "DRAFT");
	}, [versionHistory]);

	function handleLinkSuccess() {
		setLinkModalVisible(false);
		setHasLink(true);
		if (dataElementId) {
			fetchDeLinks(dataElementId).then((links) => {
				setHasLink(Array.isArray(links) && links.length > 0);
			});
		}
	}

	// ============ Navigation Handlers ============

	function handleBackToList() {
		goTo('/dsv');
	}

	function handleUpdateDataSchema() {
		const latestVersion = versionHistory.length > 0 ? versionHistory[0].version : null;
		const query = latestVersion ? `?version=${latestVersion}&mode=create` : '?mode=create';
		goTo(`/dsv/${dataElementId}/update-schema${query}`);
	}

	function handleViewSchema(version) {
		goTo(`/dsv/${dataElementId}/update-schema?version=${version.version}`);
	}

	const fieldCount = currentVersionDetail
		? getFieldCount(currentVersionDetail.elementSchemaJson || currentVersionDetail.elementSchema)
		: 0;

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			{/* Back link */}
			<div className="dsv-detail__back" onClick={handleBackToList}>
				<span className="dsv-detail__back-arrow">&larr;</span>
				<span>Back to Data Element</span>
			</div>

			{/* Header */}
			<div className="dsv-detail__header">
				<div className="dsv-detail__header-left">
					<div className="dsv-detail__header-icon">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect width="24" height="24" rx="4" fill="#EEF0FF" />
							<path d="M7 8h10M7 12h10M7 16h6" stroke="#4F5AED" strokeWidth="1.5" strokeLinecap="round" />
						</svg>
					</div>
					<div className="dsv-detail__header-info">
						<h1 className="dsv-detail__title">{currentVersionDetail?.elementName}</h1>
						<div className="dsv-detail__subtitle">Data Element Overview</div>
					</div>
				</div>
				<div className="dsv-detail__header-right">
					{canEditSchema && !hasLink && isDraftOnly && (
						<button
							className="dsv-btn dsv-btn--secondary"
							onClick={() => setLinkModalVisible(true)}
							data-testid="link-ucp-btn"
							style={{ marginRight: 8 }}
						>
							Link Use Case
						</button>
					)}
					{canEditSchema && (
						<button
							className="dsv-btn dsv-btn--primary"
							onClick={handleUpdateDataSchema}
						>
							+ Update Data Schema
						</button>
					)}
				</div>
			</div>

			{/* Description */}
			{currentVersionDetail?.changeDescription && (
				<div className="dsv-detail__description">
					{currentVersionDetail.changeDescription}
				</div>
			)}

			{/* Summary Cards */}
			{currentVersionDetail && (
				<SummaryCards
					currentVersion={currentVersionDetail.version}
					status={currentVersionDetail.status}
					totalFields={fieldCount}
					affectedOrganisations={impactAnalysis?.subscribedOrganizations || 0}
				/>
			)}

			{/* Version History */}
			<div className="dsv-version-history">
				<div className="dsv-version-history__header">
					<h2 className="dsv-version-history__title">Version History</h2>
					<p className="dsv-version-history__subtitle">Complete audit trail of all schema versions</p>
				</div>
				<div className="dsv-version-history__list">
					{versionHistory.length > 0 ? (
						versionHistory.map((version) => (
							<VersionHistoryItem
								key={version.id}
								version={version}
								onViewSchema={handleViewSchema}
							/>
						))
					) : (
						<div className="dsv-version-history__empty">
							No version history available.
						</div>
					)}
				</div>
			</div>

			<LinkUseCasePersonaModal
				visible={linkModalVisible}
				hide={() => setLinkModalVisible(false)}
				dataElement={{
					dataElementId,
					elementName: currentVersionDetail?.elementName,
				}}
				onSuccess={handleLinkSuccess}
			/>

			{/* Loading overlay */}
			{loading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src="/img/loading.gif" alt="loading" />
						</div>
					</div>,
					document.body
				)}
		</>
	);
};

export default DSVDetailPage;
